import { inject, Injectable } from "@angular/core";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { ClevertapService } from "src/app/services/clevertap.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import {
  GAME_ANALYTICS_ATTRIBUTES as ATTR,
  GAME_ANALYTICS_EVENTS as EVENTS,
  GameChallengeOutcome,
  GamePageClickInput,
} from "./game-analytics.constants";

type ChallengeDoc = Record<string, unknown> & {
  id?: string;
  title?: string;
  content?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  target?: Record<string, unknown>;
  rewards?: Array<Record<string, unknown>>;
  creation_timestamp?: unknown;
};

@Injectable({ providedIn: "root" })
export class GameAnalyticsService {
  private authService = inject(AuthService);
  private mixpanelService = inject(MixpanelService);
  private clevertapService = inject(ClevertapService);

  private issuedChallengeKeys = new Set<string>();
  private completedChallengeKeys = new Set<string>();
  private challengeStateById = new Map<string, string>();

  /** Omit null, undefined, and empty strings from analytics payloads. */
  omitEmpty(payload: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        return true;
      }),
    );
  }

  buildChallengeAttributes(challenge: ChallengeDoc): Record<string, unknown> {
    const settings = (challenge?.settings ?? {}) as Record<string, unknown>;
    const target = (challenge?.target ?? {}) as Record<string, unknown>;
    const progress = (target?.progress ?? {}) as Record<string, unknown>;
    const content = (challenge?.content ?? {}) as Record<string, unknown>;
    const primaryReward = Array.isArray(challenge?.rewards) ? challenge.rewards[0] : undefined;

    const issuedAt = this.toIsoTimestamp(
      challenge?.creation_timestamp ?? settings?.issued_timestamp ?? target?.start_date,
    );
    const timeLimitSeconds = this.resolveTimeLimitSeconds(target);

    return this.omitEmpty({
      [ATTR.GAME_CHALLENGE_ID]: challenge?.id,
      [ATTR.GAME_CHALLENGE_CATEGORY]: settings?.challenge_category ?? settings?.challenge_type,
      [ATTR.GAME_CHALLENGE_TIME_LIMIT]: timeLimitSeconds != null ? String(timeLimitSeconds) : undefined,
      [ATTR.GAME_CHALLENGE_NAME]: challenge?.title ?? content?.title,
      [ATTR.GAME_CHALLENGE_TYPE]: settings?.challenge_type,
      [ATTR.GAME_CHALLENGE_PRIZE_VALUE]: primaryReward?.value,
      [ATTR.GAME_CHALLENGE_PRIZE_TYPE]: primaryReward?.type,
      [ATTR.GAME_CHALLENGE_LEVEL_GOAL]: progress?.target,
      [ATTR.GAME_CHALLENGE_DIFFICULTY]: settings?.difficulty,
      [ATTR.GAME_CHALLENGE_TIMESTAMP_ISSUED]: issuedAt,
    });
  }

  /**
   * Terminal outcomes from the tracking sheet:
   * Won | Lost | Incomplete | Expired | Not Started.
   * Returns null while the challenge is still active.
   */
  resolveChallengeOutcome(challenge: ChallengeDoc): GameChallengeOutcome | null {
    const settings = (challenge?.settings ?? {}) as Record<string, unknown>;
    const target = (challenge?.target ?? {}) as Record<string, unknown>;
    const progress = (target?.progress ?? {}) as Record<string, unknown>;
    const achieved = Number(progress?.achieved ?? 0);
    const goal = Number(progress?.target ?? 0);
    const completed = settings?.completed === true;
    const expired = settings?.expired === true || this.isTimestampPast(target?.expiry_date);

    if (!completed && !expired) {
      return null;
    }

    // Met goal or explicitly marked complete.
    if (completed || (goal > 0 && achieved >= goal)) {
      return "Won";
    }

    // Partial progress toward a defined goal.
    if (goal > 0 && achieved > 0 && achieved < goal) {
      return "Lost";
    }

    // Never engaged, but a level goal existed.
    if (achieved === 0 && goal > 0) {
      return "Not Started";
    }

    // Engaged (progress) without a scorable goal.
    if (achieved > 0) {
      return "Incomplete";
    }

    // Timed out / expired with no progress and no level goal.
    return "Expired";
  }

  /**
   * Returns the active daily-challenge id when the played game contributes to it.
   */
  resolveGameChallengeIdForPlayedGame(
    gameData: Data,
    activeChallenges: ChallengeDoc[] | null | undefined,
    challengeLookup?: Data,
  ): string | undefined {
    if (!Array.isArray(activeChallenges) || activeChallenges.length === 0) {
      return undefined;
    }

    const gameId = gameData?.game_id ?? gameData?.parent_game_id;
    const gameTitle = gameData?.game_title ?? gameData?.game_short_title;
    const gameReference = this.normalizeReference(gameData?.reference ?? gameData?.referance_doc);

    for (const challenge of activeChallenges) {
      if (!this.isActiveDailyChallenge(challenge)) continue;
      if (this.doesGameContributeToChallenge(challenge, gameId, gameTitle, gameReference, challengeLookup)) {
        return challenge?.id;
      }
    }
    return undefined;
  }

  trackGamePageClick(input: GamePageClickInput): void {
    try {
      if (!input?.object || !input?.element) return;
      const payload = this.omitEmpty({
        [ATTR.GAME_PAGE_CLICK_OBJECT]: input.object,
        [ATTR.GAME_PAGE_CLICK_ELEMENT]: input.element,
      });
      this.sendEvent(EVENTS.GAME_PAGE_CLICK, payload);
    } catch (error) {
      this.authService?.errorHandlerLogs?.(error);
    }
  }

  trackGameChallengeIssued(challenge: ChallengeDoc): void {
    try {
      const challengeId = challenge?.id;
      if (!challengeId) return;

      const dedupeKey = `issued:${challengeId}`;
      if (this.issuedChallengeKeys.has(dedupeKey)) return;

      const settings = (challenge?.settings ?? {}) as Record<string, unknown>;
      if (settings?.issued !== true) return;
      if (settings?.expired === true) return;
      if (this.resolveChallengeOutcome(challenge)) return;

      this.issuedChallengeKeys.add(dedupeKey);
      this.sendEvent(EVENTS.GAME_CHALLENGE_ISSUED, this.buildChallengeAttributes(challenge));
    } catch (error) {
      this.authService?.errorHandlerLogs?.(error);
    }
  }

  trackGameChallengeCompleted(challenge: ChallengeDoc): void {
    try {
      const challengeId = challenge?.id;
      if (!challengeId) return;

      const outcome = this.resolveChallengeOutcome(challenge);
      if (!outcome) return;

      // Once per challenge — first terminal transition only (AC dedupe).
      const dedupeKey = `completed:${challengeId}`;
      if (this.completedChallengeKeys.has(dedupeKey)) return;

      const target = (challenge?.target ?? {}) as Record<string, unknown>;
      const progress = (target?.progress ?? {}) as Record<string, unknown>;
      const completedAt = this.toIsoTimestamp(target?.update_timestamp ?? target?.expiry_date);

      const payload = this.omitEmpty({
        ...this.buildChallengeAttributes(challenge),
        [ATTR.GAME_CHALLENGE_TIMESTAMP_COMPLETE]: completedAt,
        [ATTR.GAME_CHALLENGE_LEVEL_RESULT]: progress?.achieved,
        [ATTR.GAME_CHALLENGE_OUTCOME]: outcome,
      });

      this.completedChallengeKeys.add(dedupeKey);
      this.sendEvent(EVENTS.GAME_CHALLENGE_COMPLETED, payload);
    } catch (error) {
      this.authService?.errorHandlerLogs?.(error);
    }
  }

  /** Observe challenge collection updates and emit Issued / Completed once per terminal transition. */
  observeChallengeSnapshots(challenges: ChallengeDoc[] | null | undefined): void {
    if (!Array.isArray(challenges)) return;

    for (const challenge of challenges) {
      const challengeId = challenge?.id;
      if (!challengeId) continue;

      const outcome = this.resolveChallengeOutcome(challenge);
      const stateKey = outcome ?? "active";
      const previousState = this.challengeStateById.get(challengeId);
      const wasActive = !previousState || previousState === "active";

      if (previousState !== stateKey) {
        if (stateKey === "active") {
          this.trackGameChallengeIssued(challenge);
        } else if (outcome && wasActive) {
          this.trackGameChallengeCompleted(challenge);
        }
        this.challengeStateById.set(challengeId, stateKey);
      } else if (stateKey === "active") {
        this.trackGameChallengeIssued(challenge);
      }
    }
  }

  trackUserPlayedGame(gameData: Data, activeChallenges?: ChallengeDoc[] | null, challengeLookup?: Data): void {
    try {
      const challengeId = this.resolveGameChallengeIdForPlayedGame(gameData, activeChallenges, challengeLookup);
      const payload = this.omitEmpty({
        ...(challengeId ? { [ATTR.GAME_CHALLENGE_ID]: challengeId } : {}),
        Game_Name: gameData?.game_title,
        Game_Type: gameData?.gamer_type ?? gameData?.category,
        Game_ID: gameData?.game_id,
      });
      this.sendEvent(EVENTS.USER_PLAYED_GAME, payload);
    } catch (error) {
      this.authService?.errorHandlerLogs?.(error);
    }
  }

  private sendEvent(eventName: string, payload: Record<string, unknown>): void {
    try {
      this.authService
        ?.getUid("GameAnalyticsService")
        ?.then((uid) => {
          try {
            const body = this.omitEmpty({ ...payload, userId: uid ?? "" });
            this.mixpanelService.trackMmAnalyticsEvent(eventName, body);
            this.clevertapService.trackInlineNotifications(eventName, body);
          } catch (error) {
            this.authService?.errorHandlerLogs?.(error);
          }
        })
        .catch((error) => {
          this.authService?.errorHandlerLogs?.(error);
        });
    } catch (error) {
      this.authService?.errorHandlerLogs?.(error);
    }
  }

  private isActiveDailyChallenge(challenge: ChallengeDoc): boolean {
    const settings = (challenge?.settings ?? {}) as Record<string, unknown>;
    if (settings?.completed === true || settings?.expired === true) return false;
    const target = (challenge?.target ?? {}) as Record<string, unknown>;
    if (this.isTimestampPast(target?.expiry_date)) return false;
    return settings?.issued === true;
  }

  private doesGameContributeToChallenge(
    challenge: ChallengeDoc,
    gameId: unknown,
    gameTitle: unknown,
    gameReference: string,
    challengeLookup?: Data,
  ): boolean {
    const settings = (challenge?.settings ?? {}) as Record<string, unknown>;
    const tooltipNo = settings?.tooltip_no;
    const tooltipKey = tooltipNo != null ? String(tooltipNo) : "";
    const lookupGames =
      tooltipKey && challengeLookup?.tooltip_no?.[tooltipKey]?.game_list
        ? challengeLookup.tooltip_no[tooltipKey].game_list
        : undefined;

    if (Array.isArray(lookupGames) && lookupGames.length > 0) {
      const normalizedTitle = typeof gameTitle === "string" ? gameTitle.trim().toLowerCase() : "";
      if (lookupGames.some((name) => String(name).trim().toLowerCase() === normalizedTitle)) {
        return true;
      }
    }

    const eligibleGames = (challenge?.content as Record<string, unknown> | undefined)?.eligible_games;
    if (Array.isArray(eligibleGames)) {
      const gameIdStr = gameId != null ? String(gameId) : "";
      if (eligibleGames.some((entry) => String(entry) === gameIdStr)) return true;
    }

    const destination = settings?.destination;
    if (typeof destination === "string" && gameReference && destination.includes(gameReference)) {
      return true;
    }

    return false;
  }

  private normalizeReference(reference: unknown): string {
    if (!reference) return "";
    if (typeof reference === "string") return reference;
    const segments = (reference as Data)?._key?.path?.segments as string[] | undefined;
    if (Array.isArray(segments)) return segments.join("/");
    if (typeof (reference as Data)?.path === "string") return (reference as Data).path;
    return "";
  }

  private resolveTimeLimitSeconds(target: Record<string, unknown>): number | null {
    const start = this.toEpochSeconds(target?.start_date ?? target?.creation_timestamp);
    const end = this.toEpochSeconds(target?.expiry_date);
    if (start == null || end == null || end <= start) return null;
    return end - start;
  }

  private toEpochSeconds(value: unknown): number | null {
    if (value == null) return null;
    if (value instanceof Date) return Math.floor(value.getTime() / 1000);
    if (typeof value === "number") return Math.floor(value);
    if (typeof value === "object") {
      const ts = value as { seconds?: number; _seconds?: number };
      const seconds = ts.seconds ?? ts._seconds;
      if (typeof seconds === "number") return seconds;
    }
    return null;
  }

  private isTimestampPast(value: unknown): boolean {
    const seconds = this.toEpochSeconds(value);
    if (seconds == null) return false;
    return seconds * 1000 <= Date.now();
  }

  private toIsoTimestamp(value: unknown): string | undefined {
    const seconds = this.toEpochSeconds(value);
    if (seconds == null) return undefined;
    return new Date(seconds * 1000).toISOString();
  }
}
